import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';

import Button from '@/components/UI/mobile/V2/Button';
import ConfirmModal from '@/components/UI/mobile/V2/ConfirmModal';
import { getAxiosWithAuth } from '@/lib/axios';
import client from '@/lib/feathers';
import { getPostalItemsUrl } from '@/pages/PostalFrontDesk/constant';

import { Container } from './styles';
import type { AutoLockerNotificationType } from './types';
import { autoLockerNotificationSchema } from './types';

const selfServiceLockers = client.service('selfServiceLockers');

const AutoLockerNotification: React.FC = () => {
  const [notification, setNotification] =
    useState<AutoLockerNotificationType | null>(null);
  const [counter, setCounter] = useState(0);
  const [message, setMessage] = useState<{
    title: string;
    text: string;
  } | null>(null);

  const interval = useRef<NodeJS.Timeout | null>(null);

  const axiosWithAuth = getAxiosWithAuth();

  // 第一次進入app時，確認是否需開啟無人置物櫃通知
  const { data: fetchNotification, error } = useSWR(
    '/ezcon/api/ultraLocker/userUndecidedLocker',
    async (url: string) => {
      const { data } = await axiosWithAuth.get(url);
      const result = autoLockerNotificationSchema
        .nullable()
        .safeParse(data.data);
      if (result.success) {
        return result.data;
      }
      throw result.error;
    }
  );

  if (error) {
    console.error(error);
  }

  // 如果在進入app時有開啟無人置物櫃通知，則將通知設定到state
  useEffect(() => {
    if (!fetchNotification) {
      return;
    }
    setNotification(fetchNotification);
  }, [fetchNotification]);

  const handleClose = () => {
    setNotification(null);
    setMessage(null);
  };

  useEffect(() => {
    // 監聽無人置物櫃通知
    selfServiceLockers.on('senderPackageCheck', (data: unknown) => {
      if (typeof data !== 'string') {
        return;
      }
      const result = autoLockerNotificationSchema.safeParse(JSON.parse(data));
      if (result.success) {
        setNotification(result.data);
      } else {
        console.error(result.error);
      }
    });
    // 監聽無人置物櫃通知關閉
    selfServiceLockers.on('closeApprovalDialog', () => {
      handleClose();
    });
  }, []);

  // handle counter
  useEffect(() => {
    if (notification) {
      setCounter(moment(notification.expiredTime).diff(moment(), 'seconds'));
      interval.current = setInterval(() => {
        setCounter((prev) => {
          if (prev > 0) {
            return prev - 1;
          }
          if (interval.current) {
            clearInterval(interval.current);
            interval.current = null;
          }
          setMessage({
            title: '回覆逾時',
            text: '已超過開啟要求回覆時間',
          });
          return prev;
        });
      }, 1000);
    }
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }
    };
  }, [notification]);

  const handleAgreeRequest = async (isAgree: boolean) => {
    if (!notification) {
      return;
    }
    try {
      await axiosWithAuth.post(
        '/ezcon/api/ultraLocker/userDecisionForLockerAccess',
        { isAgree, packageId: notification.packageId }
      );
      if (isAgree) {
        setMessage({
          title: '開啟無人置物櫃',
          text: '請至無人置物櫃開啟包裹',
        });
        mutate((key) => {
          if (typeof key === 'string') {
            return key.startsWith(getPostalItemsUrl);
          }
          return false;
        });
        handleClose();
      } else {
        setMessage({
          title: '拒絕開啟無人置物櫃',
          text: '請至無人置物櫃開啟包裹',
        });
        handleClose();
      }
    } catch (error) {
      console.error(error);
      setMessage({
        title: '發生異常',
        text: '請稍後再試或與管理者聯絡',
      });
    }
  };

  return (
    <>
      {notification && (
        <Container>
          <header className="header">
            <button className="header__button" />
            <h1 className="header__title">郵件包裹</h1>
            <button className="header__button" />
          </header>
          <div className="content">
            <h2 className="content__title">包裹送達</h2>
            <div className="locker-info">
              <img
                src={notification.imageUrl}
                alt="auto locker realtime image"
                className="locker-info__img"
              />
              <div className="locker-info__content">
                <p className="info-title">同意開啟無人置物櫃</p>
                <p className="info-text">
                  回覆倒數<span>{`${counter}s`}</span>
                </p>
                <div className="locker-info__button-group">
                  <Button
                    $color="#69B49E"
                    $type="outlined"
                    onClick={() => handleAgreeRequest(false)}
                  >
                    拒絕
                  </Button>
                  <Button
                    $color="#69B49E"
                    $type="contained"
                    onClick={() => handleAgreeRequest(true)}
                  >
                    同意
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {message && (
            <ConfirmModal
              title={message.title}
              content={message.text}
              confirmText="確認"
              onConfirm={handleClose}
            />
          )}
        </Container>
      )}
    </>
  );
};

export default AutoLockerNotification;
